import { ServiceAuth } from '../hooks/useService/useService';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withE2ERequestStubbing,
  withRetries,
  withConfiguredBaseUrl,
} from '../hooks/useService';
import urls from '../util/urls';

export interface PractitionerProjectMapping {
  username: string;
  datasetId: string;
  practitionerId: string;
  default?: boolean;
}

export interface PublishAuthorConfig {
  practitionerProjectMapping: PractitionerProjectMapping[];
}

export interface EhrConfig {
  id: string;
  ehrTypes: string;
  name: string;
  additionalConfig: {
    publishable: boolean;
    publishAuthorConfig?: PublishAuthorConfig;
  };
}

export interface PublishableEhrsResponse {
  items: EhrConfig[];
}

export interface EhrIngestion {
  connectors?: Array<{ id: string; ehrId: string; project: string }>;
  records: Array<{ type: string; id: string }>;
  consumePendingUpdates?: boolean;
}

export default class EhrService {
  ehrServiceAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.ehrServiceAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getPublishableEhrs =
    (
      auth: ServiceAuth,
    ): (() => Promise<AxiosResponse<PublishableEhrsResponse>>) =>
    async () => {
      const response = await this.ehrServiceAPI(auth).get(urls.api.ehrs.ehrs());
      const publishableEhrs = response.data?.items?.filter(
        (ehrConfig: EhrConfig) => ehrConfig.additionalConfig?.publishable,
      );
      return {
        ...response,
        data: {
          items: publishableEhrs,
        },
      };
    };

  getConnectors = (auth: ServiceAuth) => async (ehrId: string) => {
    const response = await this.ehrServiceAPI(auth).get(
      urls.api.ehrs.connectors(ehrId),
    );
    return response;
  };

  postIngestion =
    (auth: ServiceAuth) => async (ehrId: string, ingestion: EhrIngestion) => {
      await this.ehrServiceAPI(auth).post(
        urls.api.ehrs.ingestions(ehrId),
        ingestion,
      );
    };
}
